import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Trash, PencilSimple, MagnifyingGlass, Database } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UNITS = ['Sqft', 'Rft', 'Nos', 'MT', 'KG', 'Ltr', 'Bag', 'Set', 'LS', 'Cum', 'Sqm', 'Rm'];
const LABOUR_TYPES = ['Skilled', 'Unskilled', 'Supervisor'];

const emptyForm = {
  description: '', trade_id: '', category: '', subcategory: '', unit: 'Sqft',
  base_rate: 0, material_percentage: '', labour_percentage: '', margin_percentage: '',
  specification: '', make: '',
  materials: [],
  manpower_entries: [],
  manpower_ref: { skilled: 0, unskilled: 0, supervisor: 0, duration_per_unit_days: 0 },
};

export const LineItemMaster = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTrade, setFilterTrade] = useState('');
  const [form, setForm] = useState({ ...emptyForm });

  const fetchData = useCallback(async () => {
    try {
      const [itemsRes, tradesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/line-item-master`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/trades`, { withCredentials: true }),
      ]);
      setItems(itemsRes.data);
      setTrades(tradesRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm({ ...emptyForm, trade_id: trades[0]?.id || '' });
    setEditingItem(null);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      description: item.description, trade_id: item.trade_id, category: item.category, subcategory: item.subcategory,
      unit: item.unit, base_rate: item.base_rate,
      material_percentage: item.material_percentage ?? '', labour_percentage: item.labour_percentage ?? '',
      margin_percentage: item.margin_percentage ?? '',
      specification: item.specification, make: item.make,
      materials: (item.materials || []).map(m => ({
        material_name: m.material_name || '', material_id: m.material_id || '',
        quantity_per_unit: m.quantity_per_unit || 0, unit: m.unit || '',
        wastage_percentage: m.wastage_percentage || 0, remarks: m.remarks || '',
      })),
      manpower_entries: (item.manpower_entries || []).map(mp => ({
        labour_type: mp.labour_type || 'Skilled', role_name: mp.role_name || '',
        manpower_per_unit: mp.manpower_per_unit || 0, remarks: mp.remarks || '',
      })),
      manpower_ref: item.manpower_ref || { skilled: 0, unskilled: 0, supervisor: 0, duration_per_unit_days: 0 },
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      material_percentage: form.material_percentage === '' ? null : parseFloat(form.material_percentage),
      labour_percentage: form.labour_percentage === '' ? null : parseFloat(form.labour_percentage),
      margin_percentage: form.margin_percentage === '' ? null : parseFloat(form.margin_percentage),
    };
    try {
      if (editingItem) {
        await axios.put(`${BACKEND_URL}/api/line-item-master/${editingItem.id}`, payload, { withCredentials: true });
        toast.success('Item updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/line-item-master`, payload, { withCredentials: true });
        toast.success('Item created');
      }
      setDialogOpen(false); resetForm(); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this master item?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/line-item-master/${id}`, { withCredentials: true });
      toast.success('Deleted'); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  /* --- Material helpers --- */
  const addMaterial = () => setForm(f => ({ ...f, materials: [...f.materials, { material_name: '', material_id: '', quantity_per_unit: 0, unit: '', wastage_percentage: 0, remarks: '' }] }));
  const updateMaterial = (idx, field, value) => {
    setForm(f => {
      const updated = [...f.materials];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...f, materials: updated };
    });
  };
  const removeMaterial = (idx) => setForm(f => ({ ...f, materials: f.materials.filter((_, i) => i !== idx) }));

  /* --- Manpower helpers --- */
  const addManpower = () => setForm(f => ({ ...f, manpower_entries: [...f.manpower_entries, { labour_type: 'Skilled', role_name: '', manpower_per_unit: 0, remarks: '' }] }));
  const updateManpower = (idx, field, value) => {
    setForm(f => {
      const updated = [...f.manpower_entries];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...f, manpower_entries: updated };
    });
  };
  const removeManpower = (idx) => setForm(f => ({ ...f, manpower_entries: f.manpower_entries.filter((_, i) => i !== idx) }));

  const filtered = items.filter(i => {
    if (filterTrade && i.trade_id !== filterTrade) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return i.description.toLowerCase().includes(q) || i.item_code.toLowerCase().includes(q) || i.category.toLowerCase().includes(q);
  });

  const categories = [...new Set(items.map(i => i.category).filter(Boolean))];

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-zinc-600">Loading...</div></div>;

  return (
    <div>
      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-zinc-800" data-testid="master-title">Line Item Master</h2>
            <p className="text-sm text-zinc-600 mt-1">{items.length} items in master database</p>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 mt-3 max-w-3xl">
              <strong>Read-only catalog.</strong> All items here are published from Master Studio (with full recipes). To add a new item, author it in <a href="/master-studio" className="underline font-semibold">Master Studio</a> → approve → publish. Team members can raise a request via <a href="/item-suggestions" className="underline font-semibold">Item Suggestions</a>.
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/item-suggestions')} variant="outline" className="border-amber-300 text-amber-700 rounded-none hover:bg-amber-50" data-testid="view-suggestions-btn">
              Suggest an Item
            </Button>
            <Button onClick={() => navigate('/master-studio')} className="bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="open-studio-btn">
              Open Master Studio
            </Button>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
            <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="text-xl font-bold">{editingItem ? 'Edit' : 'New'} Master Item</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-3">
                {/* Identity */}
                <fieldset className="border border-zinc-200 p-4">
                  <legend className="text-xs font-bold uppercase tracking-wider text-zinc-500 px-2">Identity</legend>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2"><Label className="text-xs text-zinc-500">Description *</Label>
                      <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required className="mt-1 rounded-none" data-testid="mi-description" /></div>
                    <div><Label className="text-xs text-zinc-500">Trade *</Label>
                      <select value={form.trade_id} onChange={(e) => setForm({ ...form, trade_id: e.target.value })} required
                        className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="mi-trade">
                        <option value="">Select Trade</option>
                        {trades.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select></div>
                    <div><Label className="text-xs text-zinc-500">Unit</Label>
                      <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                        className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="mi-unit">
                        {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                      </select></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div><Label className="text-xs text-zinc-500">Category</Label>
                      <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 rounded-none" list="categories-list" data-testid="mi-category" />
                      <datalist id="categories-list">{categories.map(c => <option key={c} value={c} />)}</datalist></div>
                    <div><Label className="text-xs text-zinc-500">Subcategory</Label>
                      <Input value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} className="mt-1 rounded-none" data-testid="mi-subcategory" /></div>
                  </div>
                </fieldset>

                {/* Cost */}
                <fieldset className="border border-zinc-200 p-4">
                  <legend className="text-xs font-bold uppercase tracking-wider text-zinc-500 px-2">Cost Structure</legend>
                  <div className="grid grid-cols-4 gap-4">
                    <div><Label className="text-xs text-zinc-500">Base Rate (Rs) *</Label>
                      <Input type="number" min="0" step="0.01" value={form.base_rate} onChange={(e) => setForm({ ...form, base_rate: parseFloat(e.target.value) || 0 })} required className="mt-1 rounded-none" data-testid="mi-rate" /></div>
                    <div><Label className="text-xs text-zinc-500">Material %</Label>
                      <Input type="number" min="0" max="100" value={form.material_percentage} onChange={(e) => setForm({ ...form, material_percentage: e.target.value })} className="mt-1 rounded-none" placeholder="From trade" /></div>
                    <div><Label className="text-xs text-zinc-500">Labour %</Label>
                      <Input type="number" min="0" max="100" value={form.labour_percentage} onChange={(e) => setForm({ ...form, labour_percentage: e.target.value })} className="mt-1 rounded-none" placeholder="From trade" /></div>
                    <div><Label className="text-xs text-zinc-500">Margin %</Label>
                      <Input type="number" min="0" max="100" value={form.margin_percentage} onChange={(e) => setForm({ ...form, margin_percentage: e.target.value })} className="mt-1 rounded-none" placeholder="From trade" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div><Label className="text-xs text-zinc-500">Specification</Label>
                      <Input value={form.specification} onChange={(e) => setForm({ ...form, specification: e.target.value })} className="mt-1 rounded-none" data-testid="mi-spec" /></div>
                    <div><Label className="text-xs text-zinc-500">Make / Brand</Label>
                      <Input value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} className="mt-1 rounded-none" data-testid="mi-make" /></div>
                  </div>
                </fieldset>

                {/* Material Recipe */}
                <fieldset className="border border-zinc-200 p-4" data-testid="material-recipe-section">
                  <legend className="text-xs font-bold uppercase tracking-wider text-zinc-500 px-2">Material Recipe (per unit)</legend>
                  <p className="text-[10px] text-zinc-400 mb-3">Define materials consumed per unit of this line item. Wastage % adds buffer to quantities.</p>
                  {form.materials.length > 0 && (
                    <div className="grid grid-cols-12 gap-2 mb-1.5">
                      <span className="col-span-3 text-[10px] font-semibold text-zinc-400 uppercase">Material</span>
                      <span className="col-span-2 text-[10px] font-semibold text-zinc-400 uppercase">Qty/Unit</span>
                      <span className="col-span-2 text-[10px] font-semibold text-zinc-400 uppercase">Unit</span>
                      <span className="col-span-2 text-[10px] font-semibold text-zinc-400 uppercase">Wastage %</span>
                      <span className="col-span-2 text-[10px] font-semibold text-zinc-400 uppercase">Remarks</span>
                      <span className="col-span-1"></span>
                    </div>
                  )}
                  {form.materials.map((m, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-center" data-testid={`material-row-${idx}`}>
                      <div className="col-span-3">
                        <Input placeholder="Material Name" value={m.material_name} onChange={(e) => updateMaterial(idx, 'material_name', e.target.value)} className="rounded-none text-sm h-9" data-testid={`mat-name-${idx}`} />
                      </div>
                      <div className="col-span-2">
                        <Input type="number" min="0" step="0.001" placeholder="0" value={m.quantity_per_unit} onChange={(e) => updateMaterial(idx, 'quantity_per_unit', parseFloat(e.target.value) || 0)} className="rounded-none text-sm h-9" data-testid={`mat-qty-${idx}`} />
                      </div>
                      <div className="col-span-2">
                        <Input placeholder="Sqft" value={m.unit} onChange={(e) => updateMaterial(idx, 'unit', e.target.value)} className="rounded-none text-sm h-9" data-testid={`mat-unit-${idx}`} />
                      </div>
                      <div className="col-span-2">
                        <Input type="number" min="0" max="100" step="0.1" placeholder="0" value={m.wastage_percentage} onChange={(e) => updateMaterial(idx, 'wastage_percentage', parseFloat(e.target.value) || 0)} className="rounded-none text-sm h-9" data-testid={`mat-wastage-${idx}`} />
                      </div>
                      <div className="col-span-2">
                        <Input placeholder="Optional" value={m.remarks} onChange={(e) => updateMaterial(idx, 'remarks', e.target.value)} className="rounded-none text-sm h-9" data-testid={`mat-remarks-${idx}`} />
                      </div>
                      <div className="col-span-1">
                        <Button type="button" onClick={() => removeMaterial(idx)} variant="outline" size="sm" className="rounded-none border-red-300 text-red-600 h-9 w-9 p-0" data-testid={`mat-remove-${idx}`}><Trash size={14} /></Button>
                      </div>
                    </div>
                  ))}
                  <Button type="button" onClick={addMaterial} variant="outline" size="sm" className="rounded-none border-zinc-300 text-xs mt-1" data-testid="add-material-btn">
                    <Plus size={14} className="mr-1" /> Add Material
                  </Button>
                </fieldset>

                {/* Manpower Entries */}
                <fieldset className="border border-zinc-200 p-4" data-testid="manpower-entries-section">
                  <legend className="text-xs font-bold uppercase tracking-wider text-zinc-500 px-2">Manpower Entries (per unit)</legend>
                  <p className="text-[10px] text-zinc-400 mb-3">Define manpower required per unit. This feeds into project resource planning.</p>
                  {form.manpower_entries.length > 0 && (
                    <div className="grid grid-cols-12 gap-2 mb-1.5">
                      <span className="col-span-3 text-[10px] font-semibold text-zinc-400 uppercase">Type</span>
                      <span className="col-span-3 text-[10px] font-semibold text-zinc-400 uppercase">Role Name</span>
                      <span className="col-span-2 text-[10px] font-semibold text-zinc-400 uppercase">Per Unit</span>
                      <span className="col-span-3 text-[10px] font-semibold text-zinc-400 uppercase">Remarks</span>
                      <span className="col-span-1"></span>
                    </div>
                  )}
                  {form.manpower_entries.map((mp, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-center" data-testid={`manpower-row-${idx}`}>
                      <div className="col-span-3">
                        <select value={mp.labour_type} onChange={(e) => updateManpower(idx, 'labour_type', e.target.value)}
                          className="w-full bg-zinc-50 border border-zinc-200 rounded-none p-2 text-sm h-9" data-testid={`mp-type-${idx}`}>
                          {LABOUR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div className="col-span-3">
                        <Input placeholder="e.g. Carpenter" value={mp.role_name} onChange={(e) => updateManpower(idx, 'role_name', e.target.value)} className="rounded-none text-sm h-9" data-testid={`mp-role-${idx}`} />
                      </div>
                      <div className="col-span-2">
                        <Input type="number" min="0" step="0.001" placeholder="0" value={mp.manpower_per_unit} onChange={(e) => updateManpower(idx, 'manpower_per_unit', parseFloat(e.target.value) || 0)} className="rounded-none text-sm h-9" data-testid={`mp-perunit-${idx}`} />
                      </div>
                      <div className="col-span-3">
                        <Input placeholder="Optional" value={mp.remarks} onChange={(e) => updateManpower(idx, 'remarks', e.target.value)} className="rounded-none text-sm h-9" data-testid={`mp-remarks-${idx}`} />
                      </div>
                      <div className="col-span-1">
                        <Button type="button" onClick={() => removeManpower(idx)} variant="outline" size="sm" className="rounded-none border-red-300 text-red-600 h-9 w-9 p-0" data-testid={`mp-remove-${idx}`}><Trash size={14} /></Button>
                      </div>
                    </div>
                  ))}
                  <Button type="button" onClick={addManpower} variant="outline" size="sm" className="rounded-none border-zinc-300 text-xs mt-1" data-testid="add-manpower-btn">
                    <Plus size={14} className="mr-1" /> Add Manpower
                  </Button>
                </fieldset>

                {/* Manpower Reference (legacy quick-fill) */}
                <fieldset className="border border-zinc-200 p-4">
                  <legend className="text-xs font-bold uppercase tracking-wider text-zinc-500 px-2">Manpower Reference (summary)</legend>
                  <div className="grid grid-cols-4 gap-4">
                    <div><Label className="text-xs text-zinc-500">Skilled</Label>
                      <Input type="number" min="0" value={form.manpower_ref.skilled} onChange={(e) => setForm({ ...form, manpower_ref: { ...form.manpower_ref, skilled: parseInt(e.target.value) || 0 } })} className="mt-1 rounded-none" /></div>
                    <div><Label className="text-xs text-zinc-500">Unskilled</Label>
                      <Input type="number" min="0" value={form.manpower_ref.unskilled} onChange={(e) => setForm({ ...form, manpower_ref: { ...form.manpower_ref, unskilled: parseInt(e.target.value) || 0 } })} className="mt-1 rounded-none" /></div>
                    <div><Label className="text-xs text-zinc-500">Supervisor</Label>
                      <Input type="number" min="0" value={form.manpower_ref.supervisor} onChange={(e) => setForm({ ...form, manpower_ref: { ...form.manpower_ref, supervisor: parseInt(e.target.value) || 0 } })} className="mt-1 rounded-none" /></div>
                    <div><Label className="text-xs text-zinc-500">Days/Unit</Label>
                      <Input type="number" min="0" step="0.001" value={form.manpower_ref.duration_per_unit_days} onChange={(e) => setForm({ ...form, manpower_ref: { ...form.manpower_ref, duration_per_unit_days: parseFloat(e.target.value) || 0 } })} className="mt-1 rounded-none" /></div>
                  </div>
                </fieldset>

                <Button type="submit" className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-none py-5" data-testid="mi-submit">{editingItem ? 'Update' : 'Create'} Master Item</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-4">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by description, code, or category..." className="rounded-none pl-10" data-testid="master-search" />
          </div>
          <select value={filterTrade} onChange={(e) => setFilterTrade(e.target.value)}
            className="bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="master-filter-trade">
            <option value="">All Trades</option>
            {trades.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>

        {/* Item Table */}
        {filtered.length === 0 ? (
          <div className="bg-white border border-zinc-200 p-12 text-center">
            <Database size={48} className="mx-auto text-zinc-300 mb-3" />
            <p className="text-base font-medium text-zinc-600">No items found</p>
            <p className="text-sm text-zinc-500 mt-1">Add items to build your reusable BOQ database</p>
          </div>
        ) : (
          <div className="bg-white border border-zinc-200 overflow-x-auto">
            <table className="w-full text-sm" data-testid="master-table">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  {['Code', 'Description', 'Trade', 'Category', 'Unit', 'Base Rate', 'Last Rate', 'Recipes', 'Used', ''].map(h => (
                    <th key={h} className="px-3 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-zinc-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => {
                  const matCount = (item.materials || []).length;
                  const mpCount = (item.manpower_entries || []).length;
                  return (
                    <tr key={item.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`mi-row-${item.id}`}>
                      <td className="px-3 py-3 text-xs font-mono text-zinc-600" data-testid={`mi-code-${item.id}`}>{item.item_code}</td>
                      <td className="px-3 py-3 font-medium text-zinc-800 max-w-[250px]">
                        {item.description}
                        {item.make && <span className="text-xs text-zinc-500 ml-1">({item.make})</span>}
                      </td>
                      <td className="px-3 py-3 text-xs text-zinc-600">{item.trade_name}</td>
                      <td className="px-3 py-3 text-xs">
                        {item.category && <span className="bg-zinc-100 text-zinc-600 px-1.5 py-0.5">{item.category}</span>}
                        {item.subcategory && <span className="text-zinc-500 ml-1">/ {item.subcategory}</span>}
                      </td>
                      <td className="px-3 py-3 text-xs">{item.unit}</td>
                      <td className="px-3 py-3 font-medium">Rs {item.base_rate.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-3 text-xs text-zinc-600">Rs {item.last_used_rate.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-3 text-xs">
                        {matCount > 0 && <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 mr-1" data-testid={`mi-mat-badge-${item.id}`}>{matCount} mat</span>}
                        {mpCount > 0 && <span className="bg-amber-50 text-amber-700 px-1.5 py-0.5" data-testid={`mi-mp-badge-${item.id}`}>{mpCount} mp</span>}
                        {matCount === 0 && mpCount === 0 && <span className="text-zinc-400">-</span>}
                      </td>
                      <td className="px-3 py-3 text-xs text-zinc-500">{item.usage_count}x</td>
                      <td className="px-3 py-3">
                        <div className="flex gap-1">
                          <button onClick={() => openEdit(item)} className="p-1 text-zinc-500 hover:text-blue-700" data-testid={`mi-edit-${item.id}`}><PencilSimple size={14} /></button>
                          <button onClick={() => handleDelete(item.id)} className="p-1 text-zinc-500 hover:text-red-700" data-testid={`mi-del-${item.id}`}><Trash size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

export default LineItemMaster;
